import React from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  isOpen, 
  onToggle,
  className = '' 
}) => {
  const credentialTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'education', label: 'Education' },
    { value: 'professional', label: 'Professional' },
    { value: 'identity', label: 'Identity' },
    { value: 'certification', label: 'Certification' },
    { value: 'license', label: 'License' },
    { value: 'membership', label: 'Membership' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'verified', label: 'Verified' },
    { value: 'pending', label: 'Pending' },
    { value: 'expired', label: 'Expired' },
    { value: 'revoked', label: 'Revoked' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'title', label: 'Title A-Z' },
    { value: 'issuer', label: 'Issuer A-Z' },
    { value: 'expiring', label: 'Expiring Soon' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleDateRangeChange = (field, value) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...filters?.dateRange,
        [field]: value
      }
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.search) count++;
    if (filters?.type !== 'all') count++;
    if (filters?.status !== 'all') count++;
    if (filters?.dateRange?.from || filters?.dateRange?.to) count++;
    if (filters?.showExpiringSoon) count++;
    if (filters?.showBlockchainAnchored) count++;
    return count;
  };

  return (
    <div className={`bg-background border-r border-border ${className}`}>
      {/* Mobile Toggle Button */}
      <div className="lg:hidden p-4 border-b border-border">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          fullWidth
        >
          Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
        </Button>
      </div>
      {/* Filter Content */}
      <div className={`${isOpen ? 'block' : 'hidden'} lg:block`}>
        <div className="p-4 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Filters</h3>
            {getActiveFilterCount() > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Search */}
          <div>
            <Input
              type="search"
              placeholder="Search credentials..."
              value={filters?.search}
              onChange={(e) => handleFilterChange('search', e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Credential Type */}
          <div>
            <Select
              label="Credential Type"
              options={credentialTypes}
              value={filters?.type}
              onChange={(value) => handleFilterChange('type', value)}
            />
          </div>

          {/* Status */}
          <div>
            <Select
              label="Status"
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
            />
          </div>

          {/* Sort By */}
          <div>
            <Select
              label="Sort By"
              options={sortOptions}
              value={filters?.sortBy}
              onChange={(value) => handleFilterChange('sortBy', value)}
            />
          </div>

          {/* Date Range */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Date Range</h4>
            <div className="space-y-3">
              <Input
                type="date"
                label="From"
                value={filters?.dateRange?.from}
                onChange={(e) => handleDateRangeChange('from', e?.target?.value)}
              />
              <Input
                type="date"
                label="To"
                value={filters?.dateRange?.to}
                onChange={(e) => handleDateRangeChange('to', e?.target?.value)}
              />
            </div>
          </div>

          {/* Quick Filters */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Quick Filters</h4>
            <div className="space-y-3">
              <Checkbox
                label="Expiring Soon"
                description="Show credentials expiring within 30 days"
                checked={filters?.showExpiringSoon}
                onChange={(e) => handleFilterChange('showExpiringSoon', e?.target?.checked)}
              />
              <Checkbox
                label="Blockchain Anchored"
                description="Show only blockchain-anchored credentials"
                checked={filters?.showBlockchainAnchored}
                onChange={(e) => handleFilterChange('showBlockchainAnchored', e?.target?.checked)}
              />
              <Checkbox
                label="Recently Added"
                description="Show credentials added in the last 7 days"
                checked={filters?.showRecentlyAdded}
                onChange={(e) => handleFilterChange('showRecentlyAdded', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Advanced Options */}
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-foreground mb-3">Advanced</h4>
            <div className="space-y-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                fullWidth
              >
                Export Filtered Results
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Save"
                iconPosition="left"
                fullWidth
              >
                Save Filter Preset
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;